import axios from "axios";
import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { showToast, saveAuthTokens } from "../../utill/utill";

/* ─── 소셜 아이콘 (SVG, 색상 보존)
   IconGoogle 은 GIS 네이티브 버튼을 사용하므로 별도 SVG 가 필요하지 않다. */
const IconNaver = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect width="20" height="20" rx="4" fill="#03C75A" />
    <path
      d="M11.55 10.5 8.4 6h-3v8h2.95V9.5L11.5 14h3V6h-2.95v4.5Z"
      fill="#fff"
    />
  </svg>
);

const IconKakao = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect width="20" height="20" rx="4" fill="#FEE500" />
    <path
      d="M10 4.5c-3.3 0-6 2.04-6 4.55 0 1.61 1.11 3.02 2.78 3.83l-.7 2.56c-.06.23.2.4.4.27l3.07-2.04c.15.01.3.02.45.02 3.3 0 6-2.04 6-4.64 0-2.51-2.7-4.55-6-4.55Z"
      fill="#3C1E1E"
    />
  </svg>
);

const IconApple = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M14.65 10.6c.02 2.32 2.03 3.09 2.05 3.1-.02.05-.32 1.1-1.05 2.17-.63.93-1.29 1.86-2.32 1.88-1.02.02-1.34-.6-2.5-.6-1.17 0-1.53.58-2.49.62-1 .04-1.76-1-2.4-1.93-1.3-1.87-2.3-5.28-.96-7.59.67-1.14 1.86-1.86 3.15-1.88.98-.02 1.91.66 2.51.66.6 0 1.73-.82 2.92-.7.5.02 1.9.2 2.79 1.5-.07.05-1.68.98-1.7 2.92ZM12.78 4.65a3.4 3.4 0 0 0 .77-2.43 3.5 3.5 0 0 0-2.26 1.17 3.27 3.27 0 0 0-.79 2.34 2.86 2.86 0 0 0 2.28-1.08Z"
      fill="#000"
    />
  </svg>
);

const IconRequired = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
    <path
      d="M4 0v8M0.54 2 7.46 6M0.54 6 7.46 2"
      stroke="var(--fg-anti-red, #e74c3c)"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const IconEye = ({ off = false }) =>
  off ? (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

export default function Login() {
  const accessToken = sessionStorage.getItem("access_token");
  const navigate = useNavigate();

  useEffect(() => {
    if (accessToken) navigate("/user/home");
  }, [accessToken, navigate]);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "" });
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const googleButtonRef = useRef(null);

  const handleGoogleCredential = useCallback(
    async (response) => {
      setIsGoogleLoading(true);
      setError(null);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/user/auth/google`,
          {
            credential: response.credential,
          },
        );
        await saveAuthTokens(res.data);
        navigate("/user/home");
      } catch (err) {
        const errorMessage =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          "구글 로그인 중 오류가 발생했습니다.";
        setError(errorMessage);
        showToast(errorMessage, "error");
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [navigate],
  );

  /* 구글 SDK는 백엔드 동일하게 유지하되, 디자인은 새 커스텀 버튼 사용.
     숨겨진 컨테이너에 네이티브 버튼을 렌더해 두고 커스텀 버튼 클릭 시 그것을 클릭한다. */
  useEffect(() => {
    const renderGoogleButton = () => {
      const container = googleButtonRef.current;
      if (!window.google?.accounts?.id || !container) return false;
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        auto_select: false,
        itp_support: true,
      });
      container.innerHTML = "";
      // wrapper 폭에 맞춰 그리되 GIS 가 허용하는 한도(400px) 안에서 클램프
      const wrapperWidth = container.closest(
        ".login2026__google-wrapper",
      )?.offsetWidth;
      const width = Math.min(400, Math.max(240, wrapperWidth || 360));
      window.google.accounts.id.renderButton(container, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        logo_alignment: "left",
        width,
        locale: "ko",
      });
      return true;
    };

    if (!renderGoogleButton()) {
      const id = setInterval(() => {
        if (renderGoogleButton()) clearInterval(id);
      }, 100);
      return () => clearInterval(id);
    }
    return undefined;
  }, [handleGoogleCredential]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError(null);
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /**
   * Pydantic / FastAPI가 던지는 영어 validation 메시지를 한국어로 변환.
   * 매칭되지 않는 메시지는 필드별 일반 안내 문구로 폴백.
   */
  const translateFieldError = (field, msg) => {
    const m = String(msg || "").toLowerCase();
    // 이메일 관련
    if (
      m.includes("valid email") ||
      m.includes("email address") ||
      m.includes("@-sign")
    ) {
      return "올바른 이메일 형식이 아닙니다.";
    }
    // 필수 입력
    if (m.includes("field required") || m === "field required") {
      return field === "password"
        ? "비밀번호를 입력해주세요."
        : "필수 입력 항목입니다.";
    }
    // 빈 값 / 너무 짧음
    if (
      m.includes("at least") ||
      m.includes("too_short") ||
      m.includes("min_length")
    ) {
      return field === "password"
        ? "비밀번호가 너무 짧습니다."
        : "입력값이 너무 짧습니다.";
    }
    // 너무 김
    if (
      m.includes("at most") ||
      m.includes("too_long") ||
      m.includes("max_length")
    ) {
      return "입력값이 너무 깁니다.";
    }
    // 문자열 타입
    if (m.includes("valid string") || m.includes("string_type")) {
      return "올바른 형식이 아닙니다.";
    }
    // 폴백 — 필드별 안내
    if (field === "email") return "이메일을 확인해주세요.";
    if (field === "password") return "비밀번호를 확인해주세요.";
    return "입력한 정보가 올바르지 않습니다.";
  };

  /**
   * 백엔드 detail이 영어 표현일 때 한국어로 매핑한다.
   * 매칭 실패 시 fallback 인자를 반환.
   */
  const translateDetailMessage = (detail, fallback) => {
    if (!detail || typeof detail !== "string") return fallback;
    const m = detail.toLowerCase().trim();
    if (
      m.includes("invalid credential") ||
      m.includes("invalid email or password") ||
      m.includes("incorrect email or password") ||
      m === "unauthorized"
    ) {
      return "이메일 또는 비밀번호가 올바르지 않습니다.";
    }
    if (m.includes("user not found") || m.includes("no such user")) {
      return "등록되지 않은 사용자입니다.";
    }
    if (m.includes("incorrect password") || m.includes("wrong password")) {
      return "비밀번호가 올바르지 않습니다.";
    }
    // 403 user is not active — 백엔드가 비활성 계정에 반환하는 식별자
    if (m.includes("not active")) {
      return "사용할 수 없는 계정입니다. 고객센터로 문의해 주세요.";
    }
    if (
      m.includes("account") &&
      (m.includes("disabled") ||
        m.includes("suspended") ||
        m.includes("inactive"))
    ) {
      return "비활성화된 계정입니다. 관리자에게 문의해주세요.";
    }
    if (m.includes("not verified") || m.includes("email verification")) {
      return "이메일 인증이 필요합니다.";
    }
    if (m.includes("forbidden") || m.includes("permission")) {
      return "접근 권한이 없습니다.";
    }
    if (m.includes("not found")) {
      return "요청한 리소스를 찾을 수 없습니다.";
    }
    if (m.includes("too many") || m.includes("rate limit")) {
      return "너무 많은 요청이 발생했습니다. 잠시 후 다시 시도해주세요.";
    }
    // 영문이 섞여있는지 단순 휴리스틱 — ASCII 문자 비율이 높으면 영문으로 간주
    const asciiRatio = (detail.match(/[A-Za-z]/g) || []).length / detail.length;
    if (asciiRatio > 0.5) return fallback;
    return detail;
  };

  const getErrorMessage = (err) => {
    if (!err.response) return "네트워크 연결을 확인해주세요.";
    const { status, data } = err.response;
    const detail = typeof data?.detail === "string" ? data.detail : null;
    switch (status) {
      case 400:
        return translateDetailMessage(detail, "입력한 정보를 확인해주세요.");
      case 401:
        return translateDetailMessage(
          detail,
          "이메일 또는 비밀번호가 올바르지 않습니다.",
        );
      case 403:
        return translateDetailMessage(detail, "접근 권한이 없습니다.");
      case 404:
        return translateDetailMessage(
          detail,
          "요청한 리소스를 찾을 수 없습니다.",
        );
      case 422:
        if (Array.isArray(data.detail)) {
          const errors = {};
          data.detail.forEach((d) => {
            if (d.loc && d.loc.length > 1) {
              const field = d.loc[1];
              errors[field] = translateFieldError(field, d.msg);
            }
          });
          setFieldErrors((prev) => ({ ...prev, ...errors }));
          return "입력한 정보를 확인해주세요.";
        }
        return data.detail || "입력한 정보가 올바르지 않습니다.";
      case 429:
        return "너무 많은 요청이 발생했습니다. 잠시 후 다시 시도해주세요.";
      case 500:
      case 502:
      case 503:
        return "서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.";
      default:
        return data.detail || data.message || "로그인 중 오류가 발생했습니다.";
    }
  };

  const validateBeforeSubmit = () => {
    const next = { email: "", password: "" };
    const email = formData.email.trim();
    if (!email) next.email = "이메일을 입력해주세요.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      next.email = "올바른 이메일 형식이 아닙니다.";
    if (!formData.password) next.password = "비밀번호를 입력해주세요.";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const localErrors = validateBeforeSubmit();
    if (localErrors.email || localErrors.password) {
      setFieldErrors(localErrors);
      return;
    }

    setFieldErrors({ email: "", password: "" });
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/user/login`,
        {
          email: formData.email,
          password: formData.password,
        },
      );
      await saveAuthTokens({ ...response.data, email: formData.email });
      navigate("/user/home");
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      if (err.response?.status === 401) {
        setTimeout(() => document.getElementById("password")?.focus(), 100);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // GIS 네이티브 버튼을 화면에 직접 표시하므로 별도 트리거 함수는 사용하지 않는다.

  const handleSocialNotReady = (label) => {
    showToast(`${label} 로그인은 준비 중입니다.`, "info");
  };

  return (
    <div className="login2026">
      <Helmet>
        <title>로그인 - GrowFit</title>
        <meta
          name="description"
          content="GrowFit 계정으로 로그인하세요. ChatGPT, Claude, Gemini 등 다양한 AI 모델을 활용한 실습 중심 교육 플랫폼."
        />
        <link rel="canonical" href="https://growfit.kr/login" />
        <meta property="og:title" content="로그인 - GrowFit" />
        <meta
          property="og:description"
          content="GrowFit AI 실습 플랫폼 로그인."
        />
        <meta property="og:url" content="https://growfit.kr/login" />
        <meta property="og:type" content="website" />
      </Helmet>
      <main className="login2026__main">
        <header className="login2026__hero">
          <Link
            to="/"
            className="login2026__hero-logo-link"
            aria-label="GrowFit 홈으로 이동"
          >
            <img
              className="login2026__hero-logo"
              src="/images/growfit-star.png"
              alt="GrowFit"
              width="108"
              height="120"
            />
          </Link>
          <h1 className="login2026__hero-text">
            <span className="login2026__hero-line login2026__hero-line--thin">
              배우고
            </span>
            <span className="login2026__hero-line login2026__hero-line--regular">
              만들고
            </span>
            <span className="login2026__hero-line login2026__hero-line--bold">
              사용하세요.
            </span>
          </h1>
        </header>

        <form className="login2026__form" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="login2026__error" role="alert">
              {error}
            </div>
          )}

          <div className="login2026__field">
            <label className="login2026__field-label" htmlFor="email">
              <span>이메일</span>
              <IconRequired />
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              className={`login2026__input ${fieldErrors.email ? "login2026__input--error" : ""}`}
              placeholder="example@growfit.kr"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isLoading}
              required
            />
            {fieldErrors.email && (
              <p className="login2026__field-error">{fieldErrors.email}</p>
            )}
          </div>

          <div className="login2026__field">
            <label className="login2026__field-label" htmlFor="password">
              <span>비밀번호</span>
              <IconRequired />
            </label>
            <div
              className={`login2026__input-wrap ${fieldErrors.password ? "login2026__input--error" : ""}`}
            >
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="login2026__input login2026__input--inwrap"
                placeholder="비밀번호를 입력하세요."
                value={formData.password}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />
              <button
                type="button"
                className="login2026__eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 표시"}
                tabIndex={-1}
              >
                <IconEye off={showPassword} />
              </button>
            </div>
            {fieldErrors.password && (
              <p className="login2026__field-error">{fieldErrors.password}</p>
            )}
          </div>

          <div className="login2026__row">
            <label className="login2026__check">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              <span className="login2026__check-box" aria-hidden="true">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 5 L4 7 L8 3" />
                </svg>
              </span>
              <span className="login2026__check-text">로그인 상태 유지</span>
            </label>

            <button
              type="button"
              className="login2026__link"
              onClick={() => navigate("/forgot-password")}
            >
              비밀번호 찾기
            </button>
          </div>

          <button
            type="submit"
            className="login2026__submit"
            disabled={isLoading}
          >
            {isLoading ? "로그인 중..." : "로그인"}
          </button>

          <div className="login2026__divider" aria-hidden="true">
            <span className="login2026__divider-line" />
            <span className="login2026__divider-text">또는</span>
            <span className="login2026__divider-line" />
          </div>

          <div className="login2026__socials">
            {/* Google Identity Services 네이티브 버튼 — 보안 정책상 hidden 컨테이너에서
                programmatic click 이 production 환경에서 차단되므로 직접 표시한다. */}
            <div className="login2026__google-wrapper">
              <div ref={googleButtonRef} className="login2026__google-btn" />
              {isGoogleLoading && (
                <span className="login2026__google-loading">
                  구글 로그인 중...
                </span>
              )}
            </div>

            {/* <button
              type="button"
              className="login2026__social"
              onClick={() => handleSocialNotReady('네이버')}
            >
              <IconNaver />
              <span>네이버 계정으로 로그인</span>
            </button>
            <button
              type="button"
              className="login2026__social"
              onClick={() => handleSocialNotReady('카카오')}
            >
              <IconKakao />
              <span>카카오 계정으로 로그인</span>
            </button>
            <button
              type="button"
              className="login2026__social"
              onClick={() => handleSocialNotReady('애플')}
            >
              <IconApple />
              <span>애플 계정으로 로그인</span>
            </button> */}
          </div>
        </form>

        <footer className="login2026__signup">
          <p className="login2026__signup-row">
            <span>계정이 없으신가요?</span>
            <Link to="/register" className="login2026__link">
              가입하기
            </Link>
          </p>
          <p className="login2026__terms">
            가입하시려면{" "}
            <Link to="/privacy" className="login2026__link login2026__link--sm">
              쿠키 사용
            </Link>
            을 포함해{" "}
            <Link to="/terms" className="login2026__link login2026__link--sm">
              이용약관
            </Link>
            과{" "}
            <Link to="/privacy" className="login2026__link login2026__link--sm">
              개인정보 처리방침
            </Link>
            에 동의해야 합니다.
          </p>
        </footer>
      </main>

      <nav className="login2026__footer-links" aria-label="페이지 푸터">
        <a href="mailto:support@growfit.kr">고객센터</a>
        <Link to="/terms">이용약관</Link>
        <Link to="/privacy">개인정보 처리방침</Link>
        <Link to="/privacy">쿠키 정책</Link>
        <span className="login2026__copy">ⓒ GrowFit + BCT ONE Inc.</span>
      </nav>
    </div>
  );
}
